import { useApolloClient } from '@apollo/client/react';
import { Auth } from 'aws-amplify';
import { animate, motion, useMotionValue } from 'framer-motion';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useLocation, useNavigate } from 'react-router-dom';

import CloseIcon from '~/assets/CloseIcon';
import ExpandIcon from '~/assets/ExpandIcon';
import PenIcon from '~/assets/PenIcon';
import PlusIcon from '~/assets/PlusIcon';
import {
  A2uiOperation,
  A2uiUserAction,
  computeCurrentLocationDeeplink,
  createAssistantMessage,
  createErrorMessage,
  createUserMessage,
  ERROR_MESSAGE_PREFIX,
  hydrateA2uiHistory,
  mapApiMessageToUIMessage,
  MessageCitation,
  MessagePart,
  MESSAGES_PER_PAGE,
  PdfReport,
  removeLastErrorMessage,
  SCROLL_THRESHOLD,
  UIMessage,
  USAGE_QUERY_DELAY,
} from '~/components/AIInsightsModal/AIInsightsModal.utils';
import { TriggerPayload, useBedrockStream } from '~/components/AIInsightsModal/useBedrockStream';
import { useTypewriter } from '~/components/AIInsightsModal/useTypewriter';
import A2uiSurface from '~/components/AIInsightsUI/a2ui/A2uiSurface';
import { Block } from '~/components/AIInsightsUI/blockTypes';
import ChartShimmer from '~/components/AIInsightsUI/ChartShimmer';
import { PdfReportBlock } from '~/components/AIInsightsUI/PdfReportBlock';
import { UIBlock } from '~/components/AIInsightsUI/UIBlock';
import ActionModal from '~/components/Modal/ActionModal/ActionModal';
import { conversationsPath } from '~/constants';
import { visitIQAiInsightsSidebarWidth } from '~/constants/localStorage';
import { useDirtyFormContext } from '~/contexts/DirtyFormContext';
import { useSessionContext } from '~/contexts/SessionContext';
import { useWindowDimensions } from '~/hooks/useDimensions';
import { useHorizontalDragResize } from '~/hooks/useHorizontalDragResize';
import {
  ConversationAttachment,
  ConversationRole,
  GetConversationsDocument,
  GetConversationsQuery,
  useConversationMessagesQuery,
  useCreateConversationMutation,
  useGetConversationsQuery,
  useTouchConversationAfterUserMessageMutation,
} from '~/types/api.graphql';
import { useUserPermissions } from '~/utils/permissions/UserPermissionsManager';
import { markExpandAnimation, useConversationsNav } from '~/views/ConversationsView/useConversationsNav';

import {
  ChatContainer,
  CloseButton,
  ConversationCountBadge,
  ConversationCountChevron,
  ConversationCountLabel,
  ConversationsDropdownWrapper,
  ConversationsPopoverFooter,
  ConversationsPopoverGradient,
  ConversationsPopoverItem,
  ConversationsPopoverItemTitle,
  ConversationsPopoverList,
  ConversationsPopoverListWrapper,
  ConversationsPopoverPanel,
  EditTitleButton,
  EmptyStateContainer,
  EmptyStateSubtitle,
  EmptyStateTitle,
  ExpandButton,
  ExpandedContentCenter,
  ExpandedEmptyContentBlock,
  HeaderActionsGroup,
  LoadingContainer,
  LoadingDot,
  LoadingDotsContainer,
  LoadingMoreContainer,
  LoadingSubtitle,
  LoadingTitle,
  MarkdownContent,
  MessageBubble,
  MessageContainer,
  MessagesList,
  MessagesListWrapper,
  MessagesScrollGradient,
  MobileBottomSheetContent,
  NewConversationIconButton,
  ResizeHandle,
  SidebarHeader,
  SidebarInner,
  SidebarTitleContainer,
  SidebarTitleText,
  SidebarWrapper,
  StyledBottomSheet,
  SuggestedActionsContainer,
  ToolStatusContainer,
  ToolStatusText,
  UserMessageBubble,
} from './AIInsightsSidebar.styles';
import {
  CONVERSATION_MAX_TITLE_LENGTH,
  ConversationListItem,
  CONVERSATIONS_PER_PAGE,
  getSupersededConfirmationKeys,
  renderMessageHtml,
  renderStreamingHtml,
} from './AIInsightsSidebar.utils';
import { MIN_SIDEBAR_WIDTH, useAIInsightsSidebar } from './AIInsightsSidebarContext';
import AIInsightsSidebarExpandedHeader from './AIInsightsSidebarExpandedHeader';
import AIInsightsSidebarInput, { AIInsightsSidebarInputHandle } from './AIInsightsSidebarInput';
import { ComposerAttachmentPayload, DropOverlay, SentFileCard } from './ComposerAttachment';
import { stripMentionMarkdown } from './mentionChipUtils';
import {
  MutationConfirmation,
  MutationConfirmationAnswer,
  MutationConfirmationResponse,
} from './MutationConfirmation/mutationConfirmation.types';
import MutationConfirmationCard from './MutationConfirmation/MutationConfirmationCard';
import { useSessionLimitBanner } from './SessionLimitBanner/useSessionLimitBanner';
import SuggestedActionItem from './SuggestedActionItem';
import UpdateConversationNameView, {
  UpdateConversationNameViewProps,
} from './UpdateConversationNameView/UpdateConversationNameView';
import { useAutoMode } from './useAutoMode';
import { useToolStatus } from './useToolStatus';
import { CitationCard } from './WebSearch/CitationCard';
import { citationsForPart } from './WebSearch/citationUtils';
import { SourcesFooter } from './WebSearch/SourcesFooter';
import { ThinkingSources } from './WebSearch/ThinkingSources';
import { useCitationPopover } from './WebSearch/useCitationPopover';
import { useWebSearch } from './WebSearch/useWebSearch';
import { CitationMarker } from './WebSearch/webSearch.types';

// Wraps SidebarWrapper with framer-motion for animated width transitions.
// Defined at module level so its type is stable and avoids remounts.
const MotionSidebarWrapper = motion(SidebarWrapper);

// Keys the in-flight message's parts apart from the finished messages, which are keyed by id.
const STREAMING_MESSAGE_KEY = '__streaming__';

// Width transition for the expand/collapse animation. easeOut so movement
// starts immediately and decelerates into place (no slow start, no bounce).
export const EASE_TRANSITION = { duration: 0.3, ease: 'easeOut' } as const;

const AIInsightsSidebar = () => {
  const { t } = useTranslation();
  const { isInMobile } = useWindowDimensions();
  // Context is always present here since AIInsightsSidebar is rendered inside the provider
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const ctx = useAIInsightsSidebar()!;
  const {
    isOpen,
    isExpanded,
    expandedSlotRect,
    section,
    entityDeepLink,
    conversationId,
    closeSidebar,
    sidebarWidth,
    maxSidebarWidth,
    setSidebarWidth,
    setIsResizing,
    isResizing,
    setConversationId,
    clickCounterCTA,
    skipExpandAnimation,
  } = ctx;

  const location = useLocation();
  const navigate = useNavigate();
  const { confirmIfDirty } = useDirtyFormContext();
  const { navigateAfterFirstMessage, collapseAndReturn } = useConversationsNav();

  // ---------------------------------------------------------------------------
  // Local state
  // ---------------------------------------------------------------------------
  const inputRef = useRef<AIInsightsSidebarInputHandle>(null);
  const expandedInputRef = useRef<HTMLDivElement>(null);
  const [expandedInputHeight, setExpandedInputHeight] = useState(0);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [showPopoverTopGradient, setShowPopoverTopGradient] = useState(false);
  const [showPopoverBottomGradient, setShowPopoverBottomGradient] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ top: 0, right: 0 });
  const [showMessagesTopGradient, setShowMessagesTopGradient] = useState(false);
  const [showMessagesBottomGradient, setShowMessagesBottomGradient] = useState(false);

  // ---------------------------------------------------------------------------
  // Open animation state
  // ---------------------------------------------------------------------------
  const [openAnimKey, setOpenAnimKey] = useState(0);
  const [inputInternalsVisible, setInputInternalsVisible] = useState(true);

  // ---------------------------------------------------------------------------
  // Resize handler
  // ---------------------------------------------------------------------------
  const handleResizeMouseDown = useHorizontalDragResize({
    edge: 'left',
    min: MIN_SIDEBAR_WIDTH,
    max: maxSidebarWidth,
    storageKey: visitIQAiInsightsSidebarWidth,
    width: sidebarWidth,
    onWidthChange: setSidebarWidth,
    onResizingChange: setIsResizing,
  });

  // ---------------------------------------------------------------------------
  // Chat state
  // ---------------------------------------------------------------------------
  const [messages, setMessages] = useState<UIMessage[]>([]);
  const [streamingParts, setStreamingParts] = useState<MessagePart[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [suggestedActions, setSuggestedActions] = useState<string[]>([]);
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [isA2uiLoading, setIsA2uiLoading] = useState(false);
  // Which A2UI surface occurrences the user has already submitted, keyed by `${message.id}:${partIndex}`
  // (unique per rendered surface occurrence). Durable across re-renders so only *used* surfaces stay
  // disabled; other surfaces remain interactive.
  // Seeded from persisted history on load (hydrateA2uiHistory) and unioned with live-session
  // submissions, so submitted surfaces stay disabled across page/conversation reloads.
  const [usedSurfaceKeys, setUsedSurfaceKeys] = useState<ReadonlySet<string>>(() => new Set());
  // Answers keyed by interrupt id, so a confirmation card cannot be answered twice.
  const [confirmationAnswers, setConfirmationAnswers] = useState<Record<string, MutationConfirmationAnswer>>({});

  // ---------------------------------------------------------------------------
  // Refs
  // ---------------------------------------------------------------------------
  const conversationsDropdownRef = useOnclickOutside(() => setIsPopoverOpen(false));
  const updateConversationModalRef = useRef<{ open: () => void } | null>(null);
  const popoverListRef = useRef<HTMLDivElement>(null);
  const conversationBadgeRef = useRef<HTMLButtonElement>(null);
  const messagesListRef = useRef<HTMLDivElement>(null);
  const streamingMessageRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const streamingBufferRef = useRef<string>('');
  const displayedLengthRef = useRef<number>(0);
  const isInitialLoadRef = useRef(true);
  const isLocalSessionRef = useRef(false);
  const isLoadingMoreRef = useRef(false);
  const scrollHeightBeforeLoadRef = useRef(0);
  const isSidebarOpenRef = useRef(false);
  const userScrolledUpRef = useRef(false);
  const shouldAnimateRef = useRef(false);
  const prevConversationIdRef = useRef(conversationId);
  const streamingCitationsRef = useRef<CitationMarker[]>([]);
  const streamingPartIndexRef = useRef<number>(0);
  // Set when the agent's composed surface failed validation this turn, so the turn can fall back to
  // a visible message if the agent never recomposes and never emits text either.
  const a2uiCompositionFailedRef = useRef(false);

  const [hasReceivedText, setHasReceivedText] = useState(false);

  // Session-limit usage query, dismissal state, banner + info-modal nodes.
  const { isSessionLimitReached, refetchCreditsUsage, bannerNode, infoModalNode } = useSessionLimitBanner(isOpen);

  // Citation chip popover (hover card)
  const { popover: citationPopover, handleChipMouseEnter, panelRef: citationPanelRef } = useCitationPopover();

  // ---------------------------------------------------------------------------
  // Queries & mutations
  // ---------------------------------------------------------------------------
  const {
    data: conversationsData,
    loading: isLoadingConversations,
    fetchMore: fetchMoreConversations,
  } = useGetConversationsQuery({
    variables: { first: CONVERSATIONS_PER_PAGE },
    skip: !isOpen,
    fetchPolicy: 'cache-and-network',
  });

  const {
    data,
    loading: isLoadingMessages,
    fetchMore: fetchMoreMessages,
  } = useConversationMessagesQuery({
    variables: {
      conversationId,
      first: MESSAGES_PER_PAGE,
    },
    skip: !isOpen || !conversationId,
    fetchPolicy: 'network-only',
  });

  const apolloClient = useApolloClient();
  const [createConversationMutation] = useCreateConversationMutation();
  const [touchConversationMutation] = useTouchConversationAfterUserMessageMutation();

  const moveConversationToTop = useCallback(
    (convId: string) => {
      const cached = apolloClient.readQuery<GetConversationsQuery>({
        query: GetConversationsDocument,
        variables: { first: CONVERSATIONS_PER_PAGE },
      });
      if (!cached) return;
      const nodes = cached.conversations.nodes;
      const idx = nodes.findIndex((c) => c.id === convId);
      if (idx <= 0) return; // already first or not found
      apolloClient.writeQuery<GetConversationsQuery>({
        query: GetConversationsDocument,
        variables: { first: CONVERSATIONS_PER_PAGE },
        data: {
          ...cached,
          conversations: {
            ...cached.conversations,
            nodes: [nodes[idx], ...nodes.slice(0, idx), ...nodes.slice(idx + 1)],
          },
        },
      });
    },
    [apolloClient],
  );

  const hasMoreMessages = data?.conversationMessages?.pageInfo?.hasNextPage ?? false;
  const messagesEndCursor = data?.conversationMessages?.pageInfo?.endCursor;
  const agentSessionId = data?.conversationMessages?.agentSessionId;
  const messagesConversationTitle = data?.conversationMessages?.title ?? null;

  const hasMoreConversations = conversationsData?.conversations?.pageInfo?.hasNextPage ?? false;
  const conversationsEndCursor = conversationsData?.conversations?.pageInfo?.endCursor;

  // ---------------------------------------------------------------------------
  // Derived conversation title (shown in header)
  // ---------------------------------------------------------------------------
  // Priority: preloaded list title → messages query title → fallback.
  // The list title is an instant-from-cache optimisation; messagesConversationTitle covers
  // conversations not in the preloaded list (old convos via search, page refresh).
  const currentConversationTitle = useMemo(() => {
    const listTitle = conversationsData?.conversations?.nodes?.find((c) => c.id === conversationId)?.title;
    return listTitle ?? messagesConversationTitle ?? (conversationId ? '' : t('aiInsights.newConversation'));
  }, [conversationId, conversationsData, messagesConversationTitle, t]);

  // ---------------------------------------------------------------------------
  // Scroll helpers
  // ---------------------------------------------------------------------------
  const scrollToBottom = useCallback((force = false) => {
    if (messagesListRef.current && (force || !userScrolledUpRef.current)) {
      messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
    }
  }, []);

  const updateMessagesGradients = useCallback(() => {
    const el = messagesListRef.current;
    if (!el) return;
    setShowMessagesTopGradient(el.scrollTop > 4);
    setShowMessagesBottomGradient(el.scrollTop + el.clientHeight < el.scrollHeight - 4);
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.deltaY < 0) {
      userScrolledUpRef.current = true;
    } else if (e.deltaY > 0 && messagesListRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesListRef.current;
      if (scrollHeight - scrollTop - clientHeight < 50) {
        userScrolledUpRef.current = false;
      }
    }
  }, []);

  const maintainScrollPosition = useCallback((previousScrollHeight: number) => {
    setTimeout(() => {
      if (messagesListRef.current) {
        const newScrollHeight = messagesListRef.current.scrollHeight;
        messagesListRef.current.scrollTop = newScrollHeight - previousScrollHeight;
      }
      isLoadingMoreRef.current = false;
    }, 0);
  }, []);

  // ---------------------------------------------------------------------------
  // Tool status helpers
  // ---------------------------------------------------------------------------
  const { toolStatus, toolStatusVisible, handleToolStatus: handleToolStatusBase, resetToolStatus } = useToolStatus();

  const handleToolStatus = useCallback(
    (status: string | null) => {
      if (status === null) {
        shouldAnimateRef.current = true;
        // flushSync forces a synchronous re-render so the element becomes visible right away
        // instead of waiting for React to flush the batched updates after the stream ends
        flushSync(() => {
          setHasReceivedText(true);
        });
      }
      handleToolStatusBase(status);
    },
    [handleToolStatusBase],
  );

  const streamingRefCallback = useCallback((node: HTMLDivElement | null) => {
    streamingMessageRef.current = node;
    if (node && streamingBufferRef.current.length > 0) {
      node.innerHTML = renderStreamingHtml(streamingBufferRef.current);
      displayedLengthRef.current = streamingBufferRef.current.length;
    }
  }, []);

  const handleChartLoading = useCallback(() => setIsChartLoading(true), []);

  // Close the completed trailing text run into a text part, then add the confirmation
  // card part and reset the typewriter for any post-card text.
  const handleMutationConfirmation = useCallback((confirmation: MutationConfirmation, precedingText: string) => {
    setStreamingParts((prev) => [
      ...prev,
      ...(precedingText ? [{ type: 'text' as const, text: precedingText }] : []),
      { type: 'confirmation' as const, confirmation },
    ]);
    streamingBufferRef.current = '';
    displayedLengthRef.current = 0;
    streamingPartIndexRef.current = streamingPartIndexRef.current + 1;
    if (streamingMessageRef.current) {
      streamingMessageRef.current.innerHTML = '';
    }
  }, []);

  const handleBlock = useCallback((block: Block, precedingText: string) => {
    setIsChartLoading(false);
    // Close the completed trailing text run into a text part, then add the block part.
    // precedingText comes from the stream (the buffer is already reset by the time this fires).
    // Reset the typewriter node so post-chart text streams fresh into it.
    setStreamingParts((prev) => [
      ...prev,
      ...(precedingText ? [{ type: 'text' as const, text: precedingText }] : []),
      { type: 'block' as const, block },
    ]);
    streamingBufferRef.current = '';
    displayedLengthRef.current = 0;
    // Advance the part index so the next text run's citations are scoped correctly.
    streamingPartIndexRef.current = streamingPartIndexRef.current + 1;
    if (streamingMessageRef.current) {
      streamingMessageRef.current.innerHTML = '';
    }
  }, []);

  const handleA2uiLoading = useCallback(() => setIsA2uiLoading(true), []);

  // A2UI surface arrived: close the trailing text run, append the surface as its own part
  // (mirrors handleBlock). precedingText comes from the stream (buffer already reset).
  const handleA2ui = useCallback((operations: A2uiOperation[], surfaceId: string, precedingText: string) => {
    setIsA2uiLoading(false);
    setStreamingParts((prev) => [
      ...prev,
      ...(precedingText ? [{ type: 'text' as const, text: precedingText }] : []),
      { type: 'a2ui' as const, operations, surfaceId },
    ]);
    streamingBufferRef.current = '';
    displayedLengthRef.current = 0;
    streamingPartIndexRef.current = streamingPartIndexRef.current + 1;
    if (streamingMessageRef.current) {
      streamingMessageRef.current.innerHTML = '';
    }
  }, []);

  const handlePdfReport = useCallback((report: PdfReport, precedingText: string) => {
    setIsChartLoading(false);
    // Close the completed trailing text run into a text part, then add the report part.
    // Mirrors handleBlock above.
    setStreamingParts((prev) => [
      ...prev,
      ...(precedingText ? [{ type: 'text' as const, text: precedingText }] : []),
      { type: 'pdfReport' as const, report },
    ]);
    streamingBufferRef.current = '';
    displayedLengthRef.current = 0;
    streamingPartIndexRef.current = streamingPartIndexRef.current + 1;
    if (streamingMessageRef.current) {
      streamingMessageRef.current.innerHTML = '';
    }
  }, []);

  // Invalid composition: the agent recomposes (a valid surface usually follows) or falls back to
  // plain text, so showing an error here would flash a scary message that the real surface then
  // replaces. Clear the loading shimmer, remember the failure, and let startStream render a
  // translated fallback only if the turn ends up producing nothing at all.
  const handleA2uiError = useCallback((error: string) => {
    setIsA2uiLoading(false);
    a2uiCompositionFailedRef.current = true;
    // eslint-disable-next-line no-console
    console.warn('A2UI surface rejected by validator; awaiting agent recomposition:', error);
  }, []);

  // ---------------------------------------------------------------------------
  // Hooks
  // ---------------------------------------------------------------------------
  const { startTypewriter, stopTypewriter, waitForTypewriterCompletion } = useTypewriter({
    streamingBufferRef,
    displayedLengthRef,
    streamingMessageRef,
    scrollToBottom,
    shouldAnimateRef,
    streamingCitationsRef,
    streamingPartIndexRef,
  });

  const {
    streamingThinkingSources,
    streamingSources,
    onThinkingSource,
    onSources,
    reset: resetWebSearch,
  } = useWebSearch();

  const currentLocationDeeplink = computeCurrentLocationDeeplink(entityDeepLink, location.pathname);

  const onCitation = useCallback((citation: MessageCitation) => {
    streamingCitationsRef.current = [
      ...streamingCitationsRef.current,
      {
        partIndex: citation.partIndex,
        sourceNumber: citation.sourceNumber,
        url: citation.url,
        title: citation.title,
        start: citation.start,
        end: citation.end,
        text: citation.text,
      },
    ];
  }, []);

  const { me } = useSessionContext();
  const { isAutoMode, isAutoModeReady, toggleAutoMode } = useAutoMode(me?.id);

  const { sendMessage: sendBedrockMessage } = useBedrockStream({
    sessionIdRef,
    abortControllerRef,
    streamingBufferRef,
    currentLocationDeeplink,
    isAutoMode,
    onToolStatus: handleToolStatus,
    onBlock: handleBlock,
    onThinkingSource,
    onSources,
    onCitation,
    onChartLoading: handleChartLoading,
    onA2uiLoading: handleA2uiLoading,
    onA2ui: handleA2ui,
    onA2uiError: handleA2uiError,
    onMutationConfirmation: handleMutationConfirmation,
    onPdfReport: handlePdfReport,
  });

  // ---------------------------------------------------------------------------
  // Reset helpers
  // ---------------------------------------------------------------------------
  const resetChatState = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    stopTypewriter();
    resetToolStatus();
    resetWebSearch();
    setIsSending(false);
    setIsStreaming(false);
    setIsChartLoading(false);
    setHasReceivedText(false);
    shouldAnimateRef.current = false;
    streamingBufferRef.current = '';
    displayedLengthRef.current = 0;
    streamingCitationsRef.current = [];
    streamingPartIndexRef.current = 0;
    setMessages([]);
    setSuggestedActions([]);
    setStreamingParts([]);
    setUsedSurfaceKeys(new Set());
    setConfirmationAnswers({});
    sessionIdRef.current = null;
    isInitialLoadRef.current = true;
    isLocalSessionRef.current = false;
    userScrolledUpRef.current = false;
  }, [stopTypewriter, resetToolStatus, resetWebSearch]);

  // ---------------------------------------------------------------------------
  // Navigation handlers
  // ---------------------------------------------------------------------------
  const handleSelectConversation = useCallback(
    (conv: ConversationListItem) => {
      // If already on this conversation just close the popover — don't reset
      if (conv.id === conversationId) {
        setIsPopoverOpen(false);
        setTimeout(() => inputRef.current?.focus(), 0);
        return;
      }
      // No reset of input — draft is preserved when switching conversations
      resetChatState();
      setConversationId(conv.id);
      setIsPopoverOpen(false);
    },
    [conversationId, resetChatState, setConversationId],
  );

  const handleNewConversation = useCallback(() => {
    resetChatState();
    inputRef.current?.reset();
    setConversationId(null);
    setIsPopoverOpen(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [resetChatState, setConversationId]);

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  // Reset when sidebar closes; kick off opening animations when it opens
  useEffect(() => {
    if (isOpen) {
      isSidebarOpenRef.current = true;
      setOpenAnimKey((k) => k + 1);
      if (skipExpandAnimation) {
        setInputInternalsVisible(true);
        return undefined;
      }
      setInputInternalsVisible(false);

      const id = setTimeout(() => {
        setInputInternalsVisible(true);
      }, 200);

      return () => clearTimeout(id);
    }

    isSidebarOpenRef.current = false;
    resetChatState();
    inputRef.current?.reset();
    setIsPopoverOpen(false);
    setInputInternalsVisible(true);
    return undefined;
  }, [isOpen, resetChatState, skipExpandAnimation]);

  // Reset chat state when conversationId changes via external navigation (e.g. route/URL),
  // which bypasses handleSelectConversation. Also covers navigating back to the index
  // (conversationId → null) while expanded.
  useEffect(() => {
    if (prevConversationIdRef.current === conversationId) return;
    prevConversationIdRef.current = conversationId;
    resetChatState();
  }, [conversationId, resetChatState]);

  // Load messages into state when query data arrives
  useEffect(() => {
    if (!isOpen || !data?.conversationMessages?.nodes) return;
    // Skip when the conversation was created locally this session — messages are managed by the stream
    if (isLocalSessionRef.current) return;

    const apiMessages = data.conversationMessages.nodes.map(mapApiMessageToUIMessage);
    const chronologicalMessages = [...apiMessages].reverse();
    // Replay A2UI submissions over the full merged history (re-runs on every fetchMore page,
    // so a surface and its submission landing in different pages still pair up).
    const { messages: hydratedMessages, usedSurfaceKeys: historyKeys } = hydrateA2uiHistory(chronologicalMessages);
    const wasLoadingMore = isLoadingMoreRef.current;
    const previousScrollHeight = scrollHeightBeforeLoadRef.current;

    setMessages(hydratedMessages);
    // Union: keep keys for surfaces submitted live this session.
    setUsedSurfaceKeys((prev) => (historyKeys.size > 0 ? new Set([...prev, ...historyKeys]) : prev));

    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      setTimeout(() => {
        scrollToBottom(true);
        updateMessagesGradients();
      }, 50);
    } else if (wasLoadingMore) {
      maintainScrollPosition(previousScrollHeight);
    }
    setTimeout(updateMessagesGradients, 0);
  }, [isOpen, data, scrollToBottom, maintainScrollPosition, updateMessagesGradients]);

  // Sync agentSessionId from query result into ref
  useEffect(() => {
    if (agentSessionId) {
      sessionIdRef.current = agentSessionId;
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [agentSessionId]);

  // MotionValue is the single source of truth for sidebar width. Driving it
  // imperatively (instead of via the `animate` prop) avoids framer-motion's
  // dual-source conflict — width never animates on direct page load.
  const sidebarWidthMV = useMotionValue(0);
  const prevWidthDriversRef = useRef({ isOpen, isExpanded, hasRect: expandedSlotRect != null });
  useLayoutEffect(() => {
    const expandedW = expandedSlotRect?.width ?? sidebarWidth;
    const w = isExpanded ? expandedW : isOpen ? sidebarWidth : 0;
    const prev = prevWidthDriversRef.current;
    const hasRect = expandedSlotRect != null;
    // A width-only change while already expanded — e.g. the chat nav collapsing
    // and growing the slot — must track the slot edge frame-by-frame. The slot
    // is already animating via CSS, so snap instead of firing a lagging spring
    // that trails the edge and leaves a visible gap.
    const isSlotResize = isExpanded && prev.isExpanded && prev.isOpen === isOpen && prev.hasRect === hasRect;
    prevWidthDriversRef.current = { isOpen, isExpanded, hasRect };
    // Instant on direct load (skip), live drag-resize, and slot-driven resize.
    if (skipExpandAnimation || isResizing || isSlotResize) {
      sidebarWidthMV.set(w);
      return undefined;
    }
    const controls = animate(sidebarWidthMV, w, EASE_TRANSITION);
    return () => controls.stop();
  }, [skipExpandAnimation, isExpanded, isOpen, expandedSlotRect, sidebarWidth, isResizing, sidebarWidthMV]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleLoadMore = useCallback(async () => {
    if (!hasMoreMessages || isLoadingMessages || isLoadingMore || !messagesEndCursor) return;

    setIsLoadingMore(true);
    isLoadingMoreRef.current = true;
    scrollHeightBeforeLoadRef.current = messagesListRef.current?.scrollHeight ?? 0;

    try {
      await fetchMoreMessages({
        variables: { after: messagesEndCursor },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.conversationMessages) return prev;
          return {
            ...prev,
            conversationMessages: {
              ...fetchMoreResult.conversationMessages,
              // preserve agentSessionId — fetchMore pages return null for it
              agentSessionId: prev.conversationMessages.agentSessionId,
              nodes: [
                ...(prev.conversationMessages.nodes ?? []),
                ...(fetchMoreResult.conversationMessages.nodes ?? []),
              ],
            },
          };
        },
      });
    } catch {
      isLoadingMoreRef.current = false;
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMoreMessages, isLoadingMessages, isLoadingMore, messagesEndCursor, fetchMoreMessages]);

  const handleScroll = useCallback(() => {
    if (!messagesListRef.current || isLoadingMore) return;
    if (messagesListRef.current.scrollTop < SCROLL_THRESHOLD && hasMoreMessages) {
      void handleLoadMore();
    }
    updateMessagesGradients();
  }, [hasMoreMessages, handleLoadMore, isLoadingMore, updateMessagesGradients]);

  const updatePopoverGradients = useCallback(() => {
    const el = popoverListRef.current;
    if (!el) return;
    setShowPopoverTopGradient(el.scrollTop > 0);
    setShowPopoverBottomGradient(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  const loadMoreConversations = useCallback(() => {
    if (!conversationsEndCursor) return;
    void fetchMoreConversations({
      variables: {
        first: CONVERSATIONS_PER_PAGE,
        after: conversationsEndCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.conversations) return prev;
        return {
          ...prev,
          conversations: {
            ...fetchMoreResult.conversations,
            nodes: [...prev.conversations.nodes, ...fetchMoreResult.conversations.nodes],
          },
        };
      },
    });
  }, [conversationsEndCursor, fetchMoreConversations]);

  // Recompute gradients when the popover opens or its data changes
  useEffect(() => {
    if (!isPopoverOpen) return;
    // Use rAF to let the DOM paint first
    requestAnimationFrame(updatePopoverGradients);
  }, [isPopoverOpen, conversationsData, updatePopoverGradients]);

  // Resolves `false` when the payload never reached the agent (guarded out, no session, or the send
  // failed) so callers holding un-resendable state — an A2UI surface's entered values — can recover
  // instead of discarding it. `true` once the turn was dispatched, including a user abort.
  const startStream = useCallback(
    async (
      payload: TriggerPayload,
      displayText?: string,
      displayAttachments?: ConversationAttachment[],
    ): Promise<boolean> => {
      if (isSending || isStreaming || isSessionLimitReached) return false;

      let shouldHitTouchMutation = true;

      // For new conversations (no conversationId yet): create the backend record on first send.
      // Title is seeded from the first user message excerpt.
      // Session ID = `${userId}-${conversationId}` per server-side getSessionId().
      let activeConversationId = conversationId;
      if (!conversationId && !sessionIdRef.current) {
        try {
          shouldHitTouchMutation = false;

          // Get session first — if it fails we haven't created anything yet
          const session = await Auth.currentSession();
          // A file sent alone titles the conversation by its name
          const trimmed = displayText?.trim() || displayAttachments?.[0]?.fileName;
          const titleText = trimmed ? stripMentionMarkdown(trimmed) : undefined;
          const excerpt = titleText
            ? titleText.slice(0, CONVERSATION_MAX_TITLE_LENGTH).trimEnd()
            : t('aiInsights.newConversation');
          const title = titleText && titleText.length > CONVERSATION_MAX_TITLE_LENGTH ? `${excerpt}...` : excerpt;
          const result = await createConversationMutation({
            variables: { title },
          });
          const newConv = result.data?.createConversation;
          if (!newConv) throw new Error('Failed to create conversation');
          // decodePayload() returns an any-typed map; cast explicitly to satisfy lint
          const userId = String(session.getIdToken().decodePayload().sub as unknown);
          sessionIdRef.current = userId.concat('-', newConv.id);
          isLocalSessionRef.current = true;
          setConversationId(newConv.id);
          // Pre-seed the guard so the [conversationId] reset effect treats this
          // as already-known and does not abort the in-flight send.
          prevConversationIdRef.current = newConv.id;
          activeConversationId = newConv.id;
          if (isExpanded && location.pathname === conversationsPath) {
            navigateAfterFirstMessage(newConv.id);
          }
          // Prepend new conversation to cache so it appears in the list immediately
          const cached = apolloClient.readQuery<GetConversationsQuery>({
            query: GetConversationsDocument,
            variables: { first: CONVERSATIONS_PER_PAGE },
          });
          if (cached) {
            apolloClient.writeQuery<GetConversationsQuery>({
              query: GetConversationsDocument,
              variables: { first: CONVERSATIONS_PER_PAGE },
              data: {
                ...cached,
                conversations: {
                  ...cached.conversations,
                  nodes: [newConv, ...cached.conversations.nodes],
                  totalCount: cached.conversations.totalCount + 1,
                },
              },
            });
          }
        } catch {
          setMessages((prev) => [...prev, createErrorMessage(t('aiInsights.errorSendingMessage'))]);
          return false;
        }
      }

      if (!sessionIdRef.current) return false;

      setIsSending(true);
      setIsStreaming(true);
      setIsChartLoading(false);
      setIsA2uiLoading(false);
      setSuggestedActions([]);
      setHasReceivedText(false);
      shouldAnimateRef.current = false;
      streamingBufferRef.current = '';
      displayedLengthRef.current = 0;
      streamingCitationsRef.current = [];
      streamingPartIndexRef.current = 0;
      userScrolledUpRef.current = false;
      a2uiCompositionFailedRef.current = false;
      startTypewriter();

      if (displayText || displayAttachments?.length) {
        setMessages((prev) => [
          ...removeLastErrorMessage(prev),
          createUserMessage(displayText ?? '', displayAttachments),
        ]);
      }
      setTimeout(() => scrollToBottom(true), 0);

      abortControllerRef.current = new AbortController();

      try {
        setIsSending(false);

        if (activeConversationId) {
          moveConversationToTop(activeConversationId);
          if (shouldHitTouchMutation) {
            void touchConversationMutation({
              variables: { conversationId: activeConversationId },
            });
          }
        }

        const result = await sendBedrockMessage(payload);

        if (streamingBufferRef.current.length > 0) {
          await waitForTypewriterCompletion();
        }

        if (result.parts.length > 0) {
          setMessages((prev) => [...prev, createAssistantMessage(result.parts, result.citations, result.sources)]);
        } else if (a2uiCompositionFailedRef.current) {
          // The agent's surface was rejected by the validator and it produced nothing else this
          // turn: without this the turn would end with no visible output at all. A recomposed
          // surface or plain text lands in `parts` and takes precedence, so this only shows when
          // there is genuinely nothing to render.
          setMessages((prev) => [...prev, createErrorMessage(t('aiInsights.a2ui.compositionFailed'))]);
        }
        setStreamingParts([]);
        if (result.actions.length > 0) {
          setSuggestedActions(result.actions);
          requestAnimationFrame(() => {
            setTimeout(() => scrollToBottom(true), 100);
          });
        }
      } catch (error: unknown) {
        // Abort is the user stopping their own dispatched turn — keep it `true` so an A2UI surface
        // does not reopen. A real send failure resolves `false`: the error bubble is shown and the
        // surface reopens so the user can resend. A resend that ends up writing a second submission
        // event is safe — A2uiFormSubmissionStore reads the newest one for the form.
        if ((error as Error).name === 'AbortError') return true;
        setMessages((prev) => [...prev, createErrorMessage(t('aiInsights.errorSendingMessage'))]);
        return false;
      } finally {
        stopTypewriter();
        resetToolStatus();
        resetWebSearch();
        setIsSending(false);
        setIsStreaming(false);
        setIsChartLoading(false);
        setIsA2uiLoading(false);
        setHasReceivedText(false);
        shouldAnimateRef.current = false;
        streamingBufferRef.current = '';
        displayedLengthRef.current = 0;
        streamingCitationsRef.current = [];
        streamingPartIndexRef.current = 0;
        abortControllerRef.current = null;
        setStreamingParts([]);
        // Refresh usage after a short delay so the BE has time to persist the
        // new usage before we query (runs on success, error, or abort).
        setTimeout(() => void refetchCreditsUsage(), USAGE_QUERY_DELAY);
        setTimeout(() => inputRef.current?.focus(), 0);
      }

      return true;
    },
    [
      isSending,
      isStreaming,
      isSessionLimitReached,
      conversationId,
      createConversationMutation,
      touchConversationMutation,
      moveConversationToTop,
      t,
      setConversationId,
      startTypewriter,
      stopTypewriter,
      sendBedrockMessage,
      waitForTypewriterCompletion,
      scrollToBottom,
      resetToolStatus,
      resetWebSearch,
      refetchCreditsUsage,
      isExpanded,
      location.pathname,
      navigateAfterFirstMessage,
    ],
  );

  const handleActionClick = useCallback(
    async (action: string) => {
      await startStream({ prompt: action }, action);
    },
    [startStream],
  );

  // Stable submit callback passed to AIInsightsSidebarInput — identity must not
  // change on navigation or streaming so the memoized child doesn't re-render.
  // startStream's useCallback deps include conversationId, so referencing it
  // directly would give onSubmit a fresh identity on every navigation and defeat
  // the memoized input's bail-out. Route through a live ref instead.
  // A dropped CSV is attached as **Upload files** attaches it. The handlers are always mounted, since the chat has no
  // other drop handling: without the capability they show no drop area and only stop the browser opening the file.
  const { userPermissionsManager } = useUserPermissions();
  const canAttachFiles = userPermissionsManager.canCreateAudience();
  const [isFileDragActive, setIsFileDragActive] = useState(false);
  const handleFileDragOver = useCallback(
    (event: React.DragEvent) => {
      if (!event.dataTransfer.types.includes('Files')) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = canAttachFiles ? 'copy' : 'none';
      if (canAttachFiles) setIsFileDragActive(true);
    },
    [canAttachFiles],
  );
  const handleFileDragLeave = useCallback((event: React.DragEvent) => {
    // Only leaving the sidebar itself, not moving between its children
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setIsFileDragActive(false);
  }, []);
  const handleFileDrop = useCallback(
    (event: React.DragEvent) => {
      if (!event.dataTransfer.types.includes('Files')) return;
      event.preventDefault();
      setIsFileDragActive(false);
      const file = event.dataTransfer.files[0];
      if (canAttachFiles && file) inputRef.current?.attachFile(file);
    },
    [canAttachFiles],
  );

  const startStreamRef = useRef(startStream);
  startStreamRef.current = startStream;
  const handleSubmit = useCallback((prompt: string, attachment?: ComposerAttachmentPayload) => {
    if (!attachment) {
      void startStreamRef.current({ prompt }, prompt);
      return;
    }
    void startStreamRef.current({ prompt, attachments: [attachment] }, prompt, [{ fileName: attachment.fileName }]);
  }, []);

  // A2UI surface submit: send a structured a2uiSubmission (the form's server-derived id + the
  // surface's values) instead of a prompt. No displayText → no user bubble; the rendered form
  // stays visible with the entered values. Returns whether the submission was dispatched, so the
  // surface stays interactive when it wasn't. `idempotencyKey` covers the 401-refresh retry inside
  // sendMessageWithRetry, which re-POSTs this exact payload.
  const handleA2uiSubmit = useCallback(
    (action: A2uiUserAction) =>
      startStreamRef.current({
        a2uiSubmission: {
          formId: action.surfaceId,
          values: action.context ?? {},
          idempotencyKey: crypto.randomUUID(),
        },
      }),
    [],
  );

  // Lock the surface occurrence before dispatching so a double click can't re-send, then release it
  // when the submission never reached the agent (out of AI credits, a turn still streaming, a failed
  // send). Without the release the entered values would sit in a permanently inert surface.
  const handleA2uiSurfaceSubmit = useCallback(
    async (surfaceKey: string, action: A2uiUserAction) => {
      setUsedSurfaceKeys((prev) => (prev.has(surfaceKey) ? prev : new Set(prev).add(surfaceKey)));
      const dispatched = await handleA2uiSubmit(action);
      if (!dispatched) {
        setUsedSurfaceKeys((prev) => {
          if (!prev.has(surfaceKey)) return prev;
          const next = new Set(prev);
          next.delete(surfaceKey);
          return next;
        });
      }
      return dispatched;
    },
    [handleA2uiSubmit],
  );

  const handleConfirmationRespond = useCallback(async (response: MutationConfirmationResponse): Promise<boolean> => {
    setConfirmationAnswers((prev) => ({ ...prev, [response.interruptId]: response.answer }));
    const dispatched = await startStreamRef.current({ interruptResponse: response });
    if (!dispatched) {
      setConfirmationAnswers((prev) => {
        const { [response.interruptId]: _answered, ...rest } = prev;
        return rest;
      });
    }
    return dispatched;
  }, []);

  const handleClose = useCallback(() => {
    if (isExpanded) {
      collapseAndReturn();
    } else {
      closeSidebar();
    }
  }, [isExpanded, collapseAndReturn, closeSidebar]);

  const expandNavTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (expandNavTimeoutRef.current) clearTimeout(expandNavTimeoutRef.current);
    },
    [],
  );

  const handleExpand = useCallback(() => {
    confirmIfDirty(() => {
      const { setIsExpandFading } = ctx;
      setIsExpandFading(true);
      markExpandAnimation();
      const targetPath = conversationId ? `${conversationsPath}/${conversationId}` : conversationsPath;
      expandNavTimeoutRef.current = setTimeout(() => {
        navigate(targetPath, {
          state: { origin: location.pathname + location.search },
        });
      }, 200);
    });
  }, [ctx, conversationId, navigate, location.pathname, location.search, confirmIfDirty]);

  // Intercept clicks on mention chip links inside message bubbles so
  // navigation is handled by React Router (SPA, no full-page reload).
  const handleMessageClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const chip = (e.target as HTMLElement).closest<HTMLAnchorElement>('a.mention-chip');
      if (!chip) return;
      e.preventDefault();
      const appPath = chip.getAttribute('href');
      if (appPath) navigate(appPath);
    },
    [navigate],
  );

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  // Loading only applies to existing conversations (new conversations start empty immediately)
  const isInitialLoading = !!conversationId && !isLoadingMore && (isLoadingMessages || !agentSessionId);
  const isEmpty = !isInitialLoading && messages.length === 0 && !isStreaming;

  // A submission can only be dispatched when startStream would accept it, so surfaces render dimmed
  // and inert while a turn runs or the user is out of AI credits — a dead click that silently
  // discards the entered values is worse than a visibly disabled form.
  const canSubmitA2uiSurface = !isSending && !isStreaming && !isSessionLimitReached;

  const lastAnswerableMessageIndex = useMemo(() => {
    const lastIndex = messages.length - 1;
    return messages[lastIndex]?.id.startsWith(ERROR_MESSAGE_PREFIX) ? lastIndex - 1 : lastIndex;
  }, [messages]);

  // A pending confirmation the agent sent again (after another one was answered) shows only its latest copy.
  const supersededConfirmationKeys = useMemo(
    () =>
      getSupersededConfirmationKeys([
        ...messages.map((message) => ({ key: message.id, parts: message.parts ?? [] })),
        { key: STREAMING_MESSAGE_KEY, parts: streamingParts },
      ]),
    [messages, streamingParts],
  );

  const latestSurfaceKey = useMemo(() => {
    for (let messageIndex = messages.length - 1; messageIndex >= 0; messageIndex--) {
      const parts = messages[messageIndex]?.parts ?? [];
      for (let partIndex = parts.length - 1; partIndex >= 0; partIndex--) {
        if (parts[partIndex]?.type === 'a2ui') return `${messages[messageIndex]?.id}:${partIndex}`;
      }
    }
    return null;
  }, [messages]);

  // Track expanded input height so MessagesList can pad its bottom accordingly.
  // Depends on isEmpty: the input element (expandedInputRef) only exists in the
  // non-empty branch, so it must be re-measured when the layout swaps (e.g.
  // navigating from an empty conversation to one with messages), otherwise
  // padding stays 0 and the last messages hide behind the absolute input.
  useLayoutEffect(() => {
    const el = expandedInputRef.current;
    if (!isExpanded || !el) {
      setExpandedInputHeight(0);
      return undefined;
    }

    setExpandedInputHeight(el.offsetHeight);
    const observer = new ResizeObserver(() => {
      setExpandedInputHeight(el.offsetHeight);
    });
    observer.observe(el);
    return () => observer.disconnect();
    // expandedInputRef is stable; re-run when expand state or the empty/non-empty branch changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, isEmpty]);

  const renderLoadingState = () => (
    <LoadingContainer>
      <LoadingTitle>{t('aiInsights.loadingMessages')}</LoadingTitle>
      <LoadingSubtitle>{t('aiInsights.loadingMessagesSubtitle')}</LoadingSubtitle>
      <LoadingDotsContainer>
        {[...Array(6)].map((_, i) => (
          <LoadingDot key={i} $delay={i * 0.2} />
        ))}
      </LoadingDotsContainer>
    </LoadingContainer>
  );

  const renderEmptyState = (compact = false) => (
    <EmptyStateContainer $compact={compact}>
      <motion.div
        key={`empty-title-${openAnimKey}`}
        initial={skipExpandAnimation ? false : { opacity: 0, x: -16 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4, delay: 0.25, ease: 'easeOut' }}
      >
        <EmptyStateTitle>{t('aiInsights.emptyStateTitle')}</EmptyStateTitle>
      </motion.div>
      <motion.div
        key={`empty-subtitle-${openAnimKey}`}
        initial={skipExpandAnimation ? false : { opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.75, ease: 'easeOut' }}
      >
        <EmptyStateSubtitle>{t('aiInsights.emptyStateSubtitle')}</EmptyStateSubtitle>
      </motion.div>
    </EmptyStateContainer>
  );

  const renderLoadingMore = () => (
    <LoadingMoreContainer>
      <LoadingDotsContainer>
        {[...Array(3)].map((_, i) => (
          <LoadingDot key={i} $delay={i * 0.2} />
        ))}
      </LoadingDotsContainer>
    </LoadingMoreContainer>
  );

  const renderMessages = () => (
    <>
      {isLoadingMore && renderLoadingMore()}
      {messages.map((message: UIMessage, messageIndex: number) => (
        <React.Fragment key={message.id}>
          <MessageContainer $isUser={message.role === ConversationRole.User}>
            {message.role === ConversationRole.User ? (
              <UserMessageBubble>
                {message.attachments?.map(({ fileName }) => (
                  <SentFileCard key={fileName} fileName={fileName} />
                ))}
                {message.body && (
                  <MarkdownContent
                    dangerouslySetInnerHTML={{
                      __html: renderMessageHtml(message.body),
                    }}
                  />
                )}
              </UserMessageBubble>
            ) : message.parts?.length ? (
              // Render ordered parts (text/block/text/…) in arrival order.
              // Legacy messages without parts fall back to body below.
              <MessageBubble>
                {(() => {
                  // UIMessage.citations is an intersection of codegen + local types; cast to local.
                  const allCitations = (message.citations ?? []) as MessageCitation[];
                  let blockCount = 0;
                  return message.parts.map((part, i) => {
                    if (part.type === 'text') {
                      const textIdx = blockCount;
                      const partCitations = allCitations.filter((c) => (c.partIndex ?? 0) === textIdx);
                      return (
                        <MarkdownContent
                          key={i}
                          onMouseOver={(e) => handleChipMouseEnter(e, allCitations)}
                          dangerouslySetInnerHTML={{
                            __html: renderMessageHtml(part.text, citationsForPart(partCitations)),
                          }}
                        />
                      );
                    }
                    blockCount++;
                    if (part.type === 'a2ui') {
                      const surfaceKey = `${message.id}:${i}`;
                      return (
                        <A2uiSurface
                          key={i}
                          operations={part.operations}
                          surfaceId={part.surfaceId}
                          disabled={
                            surfaceKey !== latestSurfaceKey || usedSurfaceKeys.has(surfaceKey) || !canSubmitA2uiSurface
                          }
                          onSubmit={(submitAction) => handleA2uiSurfaceSubmit(surfaceKey, submitAction)}
                        />
                      );
                    }
                    if (part.type === 'confirmation') {
                      if (supersededConfirmationKeys.has(`${message.id}:${i}`)) return null;
                      return (
                        <MutationConfirmationCard
                          key={i}
                          confirmation={part.confirmation}
                          conversationId={conversationId}
                          answer={confirmationAnswers[part.confirmation.interruptId]}
                          isActionable={
                            messageIndex === lastAnswerableMessageIndex && !isStreaming && !isSessionLimitReached
                          }
                          onRespond={handleConfirmationRespond}
                        />
                      );
                    }
                    if (part.type === 'pdfReport') {
                      return <PdfReportBlock key={i} report={part.report} />;
                    }
                    return <UIBlock key={i} block={part.block} />;
                  });
                })()}
                <SourcesFooter sources={message.sources ?? []} />
              </MessageBubble>
            ) : (
              // Legacy fallback: message has no parts (e.g. older persisted messages)
              <MessageBubble>
                <MarkdownContent
                  dangerouslySetInnerHTML={{
                    __html: renderMessageHtml(message.body ?? ''),
                  }}
                />
                <SourcesFooter sources={message.sources ?? []} />
              </MessageBubble>
            )}
          </MessageContainer>
        </React.Fragment>
      ))}
      {isStreaming && (
        <MessageContainer>
          <MessageBubble>
            {/* Render completed parts (text or chart) in arrival order */}
            {(() => {
              let blockCount = 0;
              return streamingParts.map((part, i) => {
                if (part.type === 'text') {
                  const textIdx = blockCount;
                  const partCitations = streamingCitationsRef.current.filter((c) => (c.partIndex ?? 0) === textIdx);
                  return (
                    <MarkdownContent
                      key={i}
                      onMouseOver={(e) => handleChipMouseEnter(e, streamingCitationsRef.current as MessageCitation[])}
                      dangerouslySetInnerHTML={{
                        __html: renderMessageHtml(part.text, partCitations),
                      }}
                    />
                  );
                }
                blockCount++;
                if (part.type === 'a2ui') {
                  return (
                    <A2uiSurface
                      key={i}
                      operations={part.operations}
                      surfaceId={part.surfaceId}
                      onSubmit={handleA2uiSubmit}
                      disabled
                    />
                  );
                }
                if (part.type === 'confirmation') {
                  if (supersededConfirmationKeys.has(`${STREAMING_MESSAGE_KEY}:${i}`)) return null;
                  // Inert while streaming; it becomes actionable once the stream
                  // ends and the message is finalized.
                  return (
                    <MutationConfirmationCard
                      key={i}
                      confirmation={part.confirmation}
                      conversationId={conversationId}
                      answer={confirmationAnswers[part.confirmation.interruptId]}
                      isActionable={false}
                      onRespond={handleConfirmationRespond}
                    />
                  );
                }
                if (part.type === 'pdfReport') {
                  return <PdfReportBlock key={i} report={part.report} />;
                }
                return <UIBlock key={i} block={part.block} />;
              });
            })()}
            {/* Typewriter node for the still-animating trailing text run.
                Always mounted so the typewriter can write to it; hidden until first text arrives. */}
            <MarkdownContent
              ref={streamingRefCallback}
              style={hasReceivedText ? undefined : { display: 'none' }}
              onMouseOver={(e) => handleChipMouseEnter(e, streamingCitationsRef.current as MessageCitation[])}
            />
            {/* Chart-loading shimmer: shown between chart_loading and block events. */}
            {isChartLoading && <ChartShimmer />}
            {/* A2UI-loading shimmer: shown between a2ui_loading and a2ui events. */}
            {isA2uiLoading && <ChartShimmer />}
            <SourcesFooter sources={streamingSources} />
            {!hasReceivedText && streamingThinkingSources.length > 0 && (
              <ThinkingSources sources={streamingThinkingSources} />
            )}
            {(!hasReceivedText || toolStatus) && streamingThinkingSources.length === 0 && (
              <ToolStatusContainer>
                <ToolStatusText $visible={!toolStatus || toolStatusVisible}>
                  {toolStatus ?? t('aiInsights.thinking')}
                </ToolStatusText>
              </ToolStatusContainer>
            )}
          </MessageBubble>
        </MessageContainer>
      )}
    </>
  );

  const shouldShowSuggestedActions = !isStreaming && suggestedActions.length > 0;

  const renderChatContent = () => {
    if (isInitialLoading) return renderLoadingState();
    if (isEmpty) return renderEmptyState();
    return renderMessages();
  };

  const renderConversationsPopover = () => {
    const convs = conversationsData?.conversations?.nodes ?? [];
    return (
      <ConversationsDropdownWrapper ref={conversationsDropdownRef}>
        <ConversationCountBadge
          ref={conversationBadgeRef}
          type="button"
          onClick={() => {
            if (!isPopoverOpen && conversationBadgeRef.current) {
              const rect = conversationBadgeRef.current.getBoundingClientRect();
              setPopoverPos({
                top: rect.bottom + 6,
                right: window.innerWidth - rect.right,
              });
            }
            setIsPopoverOpen((prev) => !prev);
          }}
        >
          <ConversationCountLabel>{t('aiInsights.chats')}</ConversationCountLabel>
          <ConversationCountChevron $isOpen={isPopoverOpen} />
        </ConversationCountBadge>
        <ConversationsPopoverPanel $isOpen={isPopoverOpen} $top={popoverPos.top} $right={popoverPos.right}>
          <ConversationsPopoverListWrapper>
            <ConversationsPopoverGradient $visible={showPopoverTopGradient} $position="top" />
            <ConversationsPopoverList
              id="conversationsPopoverList"
              ref={popoverListRef}
              onScroll={updatePopoverGradients}
            >
              {isLoadingConversations && convs.length === 0 ? (
                <ConversationsPopoverItemTitle as="div" style={{ padding: '0.625rem 0.875rem' }}>
                  {t('aiInsights.loading')}
                </ConversationsPopoverItemTitle>
              ) : convs.length === 0 ? (
                <ConversationsPopoverItemTitle as="div" style={{ padding: '0.625rem 0.875rem' }}>
                  {t('aiInsights.noConversationsYet')}
                </ConversationsPopoverItemTitle>
              ) : (
                <InfiniteScroll
                  dataLength={convs.length}
                  next={loadMoreConversations}
                  hasMore={hasMoreConversations}
                  loader={null}
                  scrollableTarget="conversationsPopoverList"
                  scrollThreshold="40px"
                  style={{ overflow: 'hidden' }}
                >
                  {convs.map((conv) => (
                    <ConversationsPopoverItem
                      key={conv.id}
                      type="button"
                      onClick={() => handleSelectConversation(conv)}
                    >
                      <ConversationsPopoverItemTitle>{conv.title}</ConversationsPopoverItemTitle>
                    </ConversationsPopoverItem>
                  ))}
                </InfiniteScroll>
              )}
            </ConversationsPopoverList>
            <ConversationsPopoverGradient $visible={showPopoverBottomGradient} $position="bottom" />
          </ConversationsPopoverListWrapper>
          <ConversationsPopoverFooter>{t('aiInsights.conversationStorageDisclaimer')}</ConversationsPopoverFooter>
        </ConversationsPopoverPanel>
      </ConversationsDropdownWrapper>
    );
  };

  // ---------------------------------------------------------------------------
  // Input section — single stable element reused in all render branches.
  // All primitives / stable callbacks → memo bail-out on nav / stream re-renders.
  // ---------------------------------------------------------------------------
  // Expanded landing (no messages yet) centers the empty block + input as a group.
  const emptyExpanded = isExpanded && isEmpty;

  const suggestedActionsNode = shouldShowSuggestedActions ? (
    <SuggestedActionsContainer>
      {suggestedActions.map((action, index) => (
        <SuggestedActionItem key={`action-${index}`} label={action} onClick={() => void handleActionClick(action)} />
      ))}
    </SuggestedActionsContainer>
  ) : null;

  const inputSection = (
    <AIInsightsSidebarInput
      ref={inputRef}
      section={section}
      isOpen={isOpen}
      clickCounterCTA={clickCounterCTA}
      isExpanded={isExpanded}
      isInMobile={isInMobile}
      openAnimKey={openAnimKey}
      skipExpandAnimation={skipExpandAnimation}
      inputInternalsVisible={inputInternalsVisible}
      isSending={isSending}
      isStreaming={isStreaming}
      isSessionPending={!!conversationId && !agentSessionId}
      isSessionLimitReached={isSessionLimitReached}
      banner={bannerNode}
      isAutoMode={isAutoMode}
      isAutoModeReady={isAutoModeReady}
      onToggleAutoMode={toggleAutoMode}
      canAttachFiles={canAttachFiles}
      onSubmit={handleSubmit}
    />
  );

  // ---------------------------------------------------------------------------
  // Full inner render
  // ---------------------------------------------------------------------------

  const renderInner = (fixedWidth = false) => (
    <SidebarInner
      $width={fixedWidth ? sidebarWidth : 0}
      $isExpanded={isExpanded}
      onDragEnter={handleFileDragOver}
      onDragOver={handleFileDragOver}
      onDragLeave={handleFileDragLeave}
      onDrop={handleFileDrop}
    >
      {isFileDragActive && <DropOverlay>{t('aiInsights.attachments.dropFile')}</DropOverlay>}
      <motion.div
        key={`header-${openAnimKey}`}
        initial={skipExpandAnimation ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        {isExpanded ? (
          <AIInsightsSidebarExpandedHeader
            title={currentConversationTitle}
            onCollapse={handleClose}
            onEditName={conversationId ? () => updateConversationModalRef.current?.open() : undefined}
          />
        ) : (
          <SidebarHeader>
            <NewConversationIconButton
              type="button"
              onClick={handleNewConversation}
              aria-label={t('aiInsights.newConversation')}
            >
              <PlusIcon />
            </NewConversationIconButton>
            <SidebarTitleContainer>
              <SidebarTitleText title={currentConversationTitle}>{currentConversationTitle}</SidebarTitleText>
              {conversationId && (
                <EditTitleButton
                  onClick={() => updateConversationModalRef.current?.open()}
                  aria-label={t('aiInsights.editConversationName')}
                >
                  <PenIcon />
                </EditTitleButton>
              )}
            </SidebarTitleContainer>
            {renderConversationsPopover()}
            <HeaderActionsGroup>
              {!isInMobile && (
                <ExpandButton onClick={handleExpand} aria-label={t('aiInsights.expand')}>
                  <ExpandIcon />
                </ExpandButton>
              )}
              <CloseButton onClick={handleClose} aria-label={t('common.close')}>
                <CloseIcon />
              </CloseButton>
            </HeaderActionsGroup>
          </SidebarHeader>
        )}
      </motion.div>
      <ChatContainer $isExpanded={isExpanded} $emptyExpanded={emptyExpanded}>
        {/* Content above the input swaps (centered empty block vs messages list);
            the input itself is rendered once below, in a single stable tree slot,
            so toggling isEmpty never remounts it. */}
        {emptyExpanded ? (
          <ExpandedEmptyContentBlock>
            {renderEmptyState(true)}
            {suggestedActionsNode}
          </ExpandedEmptyContentBlock>
        ) : (
          <MessagesListWrapper>
            <MessagesScrollGradient $position="top" $visible={!isExpanded && showMessagesTopGradient} />
            <MessagesList
              ref={messagesListRef}
              $isExpanded={isExpanded}
              onScroll={handleScroll}
              onWheel={handleWheel}
              onClick={handleMessageClick}
              style={isExpanded && expandedInputHeight > 0 ? { paddingBottom: `${expandedInputHeight}px` } : undefined}
            >
              {renderChatContent()}
              {!emptyExpanded && suggestedActionsNode}
            </MessagesList>
            <MessagesScrollGradient $position="bottom" $visible={!isExpanded && showMessagesBottomGradient} />
          </MessagesListWrapper>
        )}
        <ExpandedContentCenter
          $isExpanded={isExpanded}
          $emptyExpanded={emptyExpanded}
          $showTopFade={showMessagesBottomGradient}
          ref={isExpanded ? expandedInputRef : undefined}
        >
          {inputSection}
        </ExpandedContentCenter>
      </ChatContainer>
    </SidebarInner>
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const updateConversationModal = (
    <ActionModal<UpdateConversationNameViewProps>
      ref={updateConversationModalRef}
      width="25rem"
      title={t('aiInsights.editConversationNameTitle')}
      submitButtonTitle={t('common.saveChanges')}
      Component={UpdateConversationNameView}
      componentProps={{
        conversationId: conversationId ?? '',
        name: currentConversationTitle,
      }}
    />
  );

  if (isInMobile) {
    return (
      <>
        <StyledBottomSheet open={isOpen} onDismiss={closeSidebar} scrollLocking={false}>
          <MobileBottomSheetContent>{renderInner()}</MobileBottomSheetContent>
        </StyledBottomSheet>
        {updateConversationModal}
        {infoModalNode}
        {citationPopover && (
          <CitationCard
            citations={citationPopover.citations}
            layout={citationPopover.layout}
            panelRef={citationPanelRef}
          />
        )}
      </>
    );
  }

  return (
    <>
      <MotionSidebarWrapper
        $isOpen={isOpen}
        style={{ width: sidebarWidthMV as unknown as number }}
        onWheel={(e) => e.stopPropagation()}
      >
        {!isExpanded && <ResizeHandle onMouseDown={handleResizeMouseDown} />}
        {renderInner(true)}
      </MotionSidebarWrapper>
      {updateConversationModal}
      {infoModalNode}
      {citationPopover && (
        <CitationCard
          citations={citationPopover.citations}
          layout={citationPopover.layout}
          panelRef={citationPanelRef}
        />
      )}
    </>
  );
};

export default AIInsightsSidebar;
